import LISTINGSIMAGE from "../assets/Overview.jpg";
import PropTypes from "prop-types";
import React from "react";
import PdfThumbnail from "./PdfThumbnail";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import listingSource from "../Constants/listingSource";
import APPOINTMENTDEFAULTIMAGE from "../assets/Appointments.png";
import { useMatomo } from '@datapunt/matomo-tracker-react';
import RegionColors from "../Components/RegionColors";
import { categoryIcons } from "../Constants/categoryIcons";
import mediaType from "../Constants/mediaType.js";
import { ClipLoader } from "react-spinners";

function ListingsCard({ listing, terminalView, iFrame = false, isPopUp, setListingPopup }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navigateTo = (path) => {
    if (path) {
      navigate(path);
    }
  };
  const { trackEvent } = useMatomo();
  const matomoStatus = process.env.REACT_APP_MATOMO_STATUS === 'True';
  const pdfMedia = listing.media?.find(m => m.mediaType === mediaType.PDF && m.signedUrl);

  const [imageLoaded, setImageLoaded] = React.useState(false);

  const handleListingClick = () => {
    if (matomoStatus) {
      trackEvent({
        category: 'Listing',
        action: 'Click',
        name: listing.title,
        value: listing.id,
      });
    }

    if (listing.source === listingSource.INSTAGRAM && listing.externalId && listing.externalId.startsWith('https://www.instagram.com')) {
      window.open(listing.website, '_blank');
    }
    else if (iFrame) {
      navigateTo(`/IframeListing?listingId=${listing.id}`
      );
    } else if (
      listing.source === listingSource.USER_ENTRY ||
      listing.showExternal === 0
    ) {
      if (isPopUp) {
        setListingPopup(listing);
        return
      } else {
        const url = `/Listing?listingId=${listing.id}${terminalView ? "&terminalView=true" : ""}`;
        navigateTo(url);
      }
    } else if (
      (listing.source === listingSource.SCRAPER &&
        listing.showExternal === 1) ||
      listing.source === listingSource.INSTAGRAM
    ) {
      window.location.href = listing.website; // web scraper and Instagram
    } else {
      window.location.href = listing.website;
    }
  };

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        handleListingClick();
      }}
      className="w-full bg-white shadow-[0_3px_10px_rgb(0,0,0,0.2)] rounded-2xl overflow-hidden transition-transform duration-300 hover:translate-y-1 cursor-pointer flex flex-col h-full"
    >
      <div className="relative h-64 w-full flex-shrink-0">
        {pdfMedia ? (
          <PdfThumbnail pdfUrl={pdfMedia.signedUrl} />
        ) : listing.media?.some(m => m.mediaType === mediaType.IMAGE && m.signedUrl) ? (
          <>
            {!imageLoaded && (
              <div className="flex justify-center items-center h-[300px]">
                <ClipLoader color="#36D7B7" size={40} />
              </div>
            )}
            <img
              alt="Listing"
              className={`object-cover object-center h-full w-full rounded-t-2xl transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              src={listing.media.find(m => m.mediaType === mediaType.IMAGE && m.signedUrl)?.signedUrl}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = listing.appointmentId ? APPOINTMENTDEFAULTIMAGE : LISTINGSIMAGE;
                setImageLoaded(true);
              }}
            />
          </>
        ) : (
          <img
            alt="Listing"
            className="object-cover object-center h-full w-full rounded-t-2xl"
            src={LISTINGSIMAGE}
          />
        )}

        <svg className="absolute bottom-0 left-0 w-full text-white -mb-1" viewBox="0 0 1440 320">
          <path fill="currentColor" d="M0,256 C480,400 960,112 1440,256L1440,320L0,320Z"></path>
        </svg>
      </div>

      <div className="p-6 bg-white rounded-b-2xl flex-grow flex flex-col h-full relative">
        {listing.categoryId && categoryIcons[listing.categoryId] && (
          <button className={`${RegionColors.darkBgColorV2} absolute -top-5 left-4 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg border-4 border-white`}>
            <svg
              className="h-5 w-5 fill-current text-white"
              xmlns="http://www.w3.org/2000/svg"
              viewBox={categoryIcons[listing.categoryId]?.viewBox || "0 0 512 512"}
            >
              <path d={categoryIcons[listing.categoryId].svgIcon} />
            </svg>
          </button>
        )}

        <h2 className="text-lg md:text-2xl font-bold text-gray-800 truncate mt-4" style={{ fontFamily: "'Space Grotesk', Helvetica, Arial, Lucida, sans-serif" }}>
          {listing.title}
        </h2>

        {(listing.startDate || listing.endDate) ? (
          <p className="text-gray-600 text-sm mt-2 font-medium">
            {listing.startDate && (
              <>
                {new Date(listing.startDate.slice(0, 10)).toLocaleDateString('de-DE')} (
                {new Date(listing.startDate.replace('Z', '')).toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'Europe/Berlin',
                })}
                )
              </>
            )}
            {listing.startDate && listing.endDate && (
              <span className="text-gray-800 font-semibold"> {t('To')} </span>
            )}
            {listing.endDate && (
              <>
                {new Date(listing.endDate.slice(0, 10)).toLocaleDateString('de-DE')} (
                {new Date(listing.endDate.replace('Z', '')).toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'Europe/Berlin',
                })}
                )
              </>
            )}
          </p>
        ) : (
          <p
            className="text-gray-600 text-sm mt-2 font-medium line-clamp-3 flex-grow"
            style={{ minHeight: '60px' }}
            dangerouslySetInnerHTML={{ __html: listing.description }}
          />
        )}
      </div>
    </div>
  );
}

ListingsCard.propTypes = {
  listing: PropTypes.object.isRequired,
  terminalView: PropTypes.bool,
  iFrame: PropTypes.bool
};

export default ListingsCard;